import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Page, Stepper } from '../components/Layout'
import { ErrorBox, Field, TextInput } from '../components/ui'
import { apiPost } from '../lib/api'
import { getFunnel } from '../lib/funnel'

interface ContratacaoResp {
  caso_id: string
  contrato_status: string
  valor_entrada: number
  clicksign_envelope_id: string | null
  ativacao_url: string | null
  ja_ativado?: boolean
}

export default function Contratacao() {
  const f = getFunnel()
  const [erro, setErro] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [resp, setResp] = useState<ContratacaoResp | null>(null)

  const [nome, setNome] = useState(f.nome ?? '')
  const [email, setEmail] = useState(f.email ?? '')
  const [cpf, setCpf] = useState('')
  const [telefone, setTelefone] = useState(f.telefone ?? '')
  const [empreendimento, setEmpreendimento] = useState('')
  const [construtora, setConstrutora] = useState('')
  const [valorParcela, setValorParcela] = useState(f.valor_parcela ? String(f.valor_parcela) : '')
  const [dataPrometida, setDataPrometida] = useState(f.data_entrega_prometida ?? '')
  // dados pessoais (para procuração + contrato)
  const [rg, setRg] = useState('')
  const [profissao, setProfissao] = useState('')
  const [estadoCivil, setEstadoCivil] = useState('')
  const [naturalidade, setNaturalidade] = useState('brasileiro(a)')
  // endereço
  const [rua, setRua] = useState('')
  const [numero, setNumero] = useState('')
  const [complemento, setComplemento] = useState('')
  const [bairro, setBairro] = useState('')
  const [cidade, setCidade] = useState('')
  const [uf, setUf] = useState('')

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    setErro(null)
    if (!f.lead_id || !f.proposta_id) {
      setErro('Sessão do funil não encontrada. Refaça a triagem e a proposta.')
      return
    }
    setLoading(true)
    try {
      const r = await apiPost<ContratacaoResp>('/api/contratacao/iniciar', {
        lead_id: f.lead_id,
        proposta_id: f.proposta_id,
        cliente: {
          nome,
          cpf,
          email,
          telefone,
          rg,
          profissao,
          estado_civil: estadoCivil,
          naturalidade,
          endereco: { rua, numero, complemento, bairro, cidade, uf },
        },
        caso: {
          valor_parcela: Number(valorParcela.replace(',', '.')) || 0,
          empreendimento,
          construtora,
          data_entrega_prometida: dataPrometida || undefined,
        },
      })
      setResp(r)
    } catch (e2) {
      setErro((e2 as Error).message)
    } finally {
      setLoading(false)
    }
  }

  if (resp) {
    return (
      <Page>
        <Stepper step={2} />
        <div className="card">
          <h1 className="text-2xl font-semibold text-pfa-navy">Contratação iniciada</h1>
          <p className="mt-2 text-pfa-slate">
            {resp.clicksign_envelope_id
              ? 'Enviamos a procuração e o contrato de honorários para o seu e-mail, via assinatura digital (ClickSign).'
              : 'Em instantes você receberá a procuração e o contrato de honorários para assinatura digital.'}
          </p>
          <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-pfa-slate">
            <li>Assine os documentos enviados por e-mail.</li>
            {resp.ja_ativado ? (
              <li>
                Você já tem acesso — entre em{' '}
                <Link to="/painel" className="text-pfa-coral underline">
                  Meu caso
                </Link>{' '}
                para acompanhar este e os seus outros casos.
              </li>
            ) : (
              <li>
                Ative seu acesso para acompanhar o caso:{' '}
                <a className="text-pfa-coral underline" href={resp.ativacao_url ?? '#'}>
                  ativar minha conta
                </a>
                .
              </li>
            )}
            <li>Envie os documentos solicitados no portal.</li>
          </ol>
          <p className="mt-4 text-xs text-pfa-slate/70">
            Lembrando: <strong>nada é cobrado agora.</strong> A entrada só é cobrada se a Justiça mandar pausar as suas
            parcelas — e só alguns dias depois disso.
          </p>
        </div>
      </Page>
    )
  }

  return (
    <Page>
      <Stepper step={2} />
      <h1 className="text-2xl font-semibold text-pfa-navy">Contratação</h1>
      <form onSubmit={enviar} className="mt-6 grid gap-6 md:grid-cols-2">
        <div className="card">
          <h2 className="mb-4 font-semibold text-pfa-navy">Seus dados</h2>
          <Field label="Nome completo">
            <TextInput value={nome} onChange={(e) => setNome(e.target.value)} required />
          </Field>
          <Field label="CPF" hint="Somente para procuração e contrato.">
            <TextInput value={cpf} onChange={(e) => setCpf(e.target.value)} required />
          </Field>
          <Field label="E-mail">
            <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <Field label="Telefone / WhatsApp">
            <TextInput value={telefone} onChange={(e) => setTelefone(e.target.value)} />
          </Field>
          <Field label="Nacionalidade">
            <TextInput value={naturalidade} onChange={(e) => setNaturalidade(e.target.value)} />
          </Field>
          <Field label="Estado civil">
            <TextInput value={estadoCivil} onChange={(e) => setEstadoCivil(e.target.value)} placeholder="solteiro(a), casado(a)…" />
          </Field>
          <Field label="Profissão">
            <TextInput value={profissao} onChange={(e) => setProfissao(e.target.value)} />
          </Field>
          <Field label="RG" hint="Para a procuração e o contrato.">
            <TextInput value={rg} onChange={(e) => setRg(e.target.value)} />
          </Field>
        </div>

        <div className="card">
          <h2 className="mb-4 font-semibold text-pfa-navy">O imóvel</h2>
          <Field label="Empreendimento">
            <TextInput value={empreendimento} onChange={(e) => setEmpreendimento(e.target.value)} />
          </Field>
          <Field label="Construtora / Incorporadora">
            <TextInput value={construtora} onChange={(e) => setConstrutora(e.target.value)} />
          </Field>
          <Field label="Valor da parcela mensal (R$)">
            <TextInput inputMode="decimal" value={valorParcela} onChange={(e) => setValorParcela(e.target.value)} required />
          </Field>
          <Field label="Data de entrega prometida">
            <TextInput type="date" value={dataPrometida} onChange={(e) => setDataPrometida(e.target.value)} />
          </Field>
        </div>

        <div className="card md:col-span-2">
          <h2 className="mb-4 font-semibold text-pfa-navy">Endereço</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Rua / Logradouro">
              <TextInput value={rua} onChange={(e) => setRua(e.target.value)} />
            </Field>
            <Field label="Número">
              <TextInput value={numero} onChange={(e) => setNumero(e.target.value)} />
            </Field>
            <Field label="Complemento">
              <TextInput value={complemento} onChange={(e) => setComplemento(e.target.value)} />
            </Field>
            <Field label="Bairro">
              <TextInput value={bairro} onChange={(e) => setBairro(e.target.value)} />
            </Field>
            <Field label="Cidade">
              <TextInput value={cidade} onChange={(e) => setCidade(e.target.value)} />
            </Field>
            <Field label="UF">
              <TextInput value={uf} onChange={(e) => setUf(e.target.value)} maxLength={2} />
            </Field>
          </div>
        </div>

        <div className="md:col-span-2">
          <ErrorBox message={erro} />
          <button className="btn-accent mt-2" disabled={loading} type="submit">
            {loading ? 'Processando…' : 'Avançar'}
          </button>
        </div>
      </form>
    </Page>
  )
}
